import { createFileRoute, redirect, useSearch } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';
import { useUser } from '@/hooks/queries/auth';
import { getSafePostLoginRedirect } from '@/lib/authRedirect';
import { ENV } from '@/config/env';
import type { User } from '@/types';

export const Route = createFileRoute('/authentikLogin')({
  component: LoginPage,
  beforeLoad: ({ search }) => {
    const user = useUser.getState().user;
    const rawRedirectTo = (search as { redirectTo?: string })?.redirectTo;
    const safeRedirectTo = getSafePostLoginRedirect(rawRedirectTo);
    if (user) {
      throw redirect({
        to: safeRedirectTo,
        replace: true,
        from: '/authentikLogin',
      });
    }
  },
});

function LoginPage() {
  const search = useSearch({ from: '/authentikLogin' }) as { redirectTo?: string; forceLogin?: boolean };
  const authorizeUrl = ENV.AUTHENTIK.AUTHORIZE_URL;
  const clientId = ENV.AUTHENTIK.CLIENT_ID;
  const redirectUri = ENV.AUTHENTIK.REDIRECT_URI;
  const scope = ENV.AUTHENTIK.SCOPE;

  useEffect(() => {
    if (!authorizeUrl || !clientId || !redirectUri) {
      return;
    }

    const redirectToAuthentik = async () => {
      const randomBytes = new Uint8Array(32);
      crypto.getRandomValues(randomBytes);
      const state = Array.from(randomBytes)
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('');

      const verifierBytes = new Uint8Array(32);
      crypto.getRandomValues(verifierBytes);
      const codeVerifier = Array.from(verifierBytes)
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('');

      const challengeBuffer = await crypto.subtle.digest(
        'SHA-256',
        new TextEncoder().encode(codeVerifier)
      );
      const challengeArray = Array.from(new Uint8Array(challengeBuffer));
      const codeChallenge = btoa(String.fromCharCode(...challengeArray))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');

      sessionStorage.setItem('auth_state', state);
      sessionStorage.setItem('auth_code_verifier', codeVerifier);
      if (search?.redirectTo) {
        sessionStorage.setItem('auth_redirect_to', getSafePostLoginRedirect(search.redirectTo));
      }

      const params = new URLSearchParams({
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: 'code',
        scope,
        state,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
      });
      
      // If user just logged out, force them to re-authenticate
      // prompt=login: Show login screen even if session exists
      // max_age=0: Invalidate any existing session and force re-authentication
      if (search?.forceLogin) {
        params.set('prompt', 'login');
        params.set('max_age', '0');
        // Request the default ACR to reinforce MFA if configured
        params.set('acr_values', 'goauthentik.io/providers/oauth2/default');
      }
      
      if (import.meta.env.DEV) {
        console.log('Redirecting to Authentik with params:', Object.fromEntries(params));
      }
      window.location.href = `${authorizeUrl}?${params.toString()}`;
    };

    redirectToAuthentik();
  }, [authorizeUrl, clientId, redirectUri, scope, search?.redirectTo, search?.forceLogin]);
  const year = useMemo(() => new Date().getFullYear(), []);

  return (
    <div className="flex min-h-screen w-full flex-col md:grid md:grid-cols-12">
      {/* Left Panel: Illustration */}
      <div
        className="flex h-full flex-col justify-between p-8 md:col-span-6 md:flex"
        style={{ background: 'var(--foreground)', color: 'var(--primary-foreground)' }}
      >
        {/* Logo and Tagline */}
        <div>
          <div className="flex items-center gap-2">
            <img src="/android-chrome-192x192.png" alt="Mani" className="h-10 w-10" />
            <h1
              className="mb-2 text-3xl font-extrabold tracking-tight"
              style={{ letterSpacing: '-0.04em' }}
            >
              Mani
            </h1>
          </div>
          <p className="text-lg font-medium opacity-80">Your Project & Task HQ</p>
        </div>
        {/* Illustration */}
        <div className="flex flex-1 flex-col items-center justify-center">
          <div className="relative h-48 w-72">
            {/* Main card */}
            <div className="absolute top-1/2 left-1/2 w-56 -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white/90 p-6 shadow-lg">
              <p className="text-xl font-bold text-black">24 Tasks Completed</p>
              <div className="mt-2 h-2 w-full rounded-full bg-gray-200">
                <div className="h-2 rounded-full bg-[var(--accent)]" style={{ width: '80%' }} />
              </div>
              <p className="mt-2 text-xs text-gray-500">5 Projects Active</p>
            </div>
            {/* Floating icons/cards */}
            <div className="absolute top-4 left-0 rounded-lg bg-white/80 p-2 shadow">
              <span role="img" aria-label="Tasks">📝</span>
            </div>
            <div className="absolute top-0 right-0 rounded-lg bg-white/80 p-2 shadow">
              <span role="img" aria-label="Projects">📁</span>
            </div>
            <div className="absolute bottom-0 left-8 rounded-lg bg-white/80 p-2 shadow">
              <span role="img" aria-label="Teams">👥</span>
            </div>
            <div className="absolute right-8 bottom-2 rounded-lg bg-white/80 p-2 shadow">
              <span role="img" aria-label="Mobile">📱</span>
            </div>
          </div>
        </div>
        {/* Footer */}
        <div className="mt-8 text-xs opacity-70">Used by teams worldwide</div>
      </div>
      {/* Right Panel: Auth redirect */}
      <div className="col-span-6 flex w-full items-center justify-center bg-[var(--background)] py-8 md:py-0">
        <div className="flex w-full max-w-sm flex-col items-center justify-center gap-4 rounded-lg bg-white/95 p-4 sm:p-6 md:p-8 border border-[var(--border)] shadow-sm">
          <h2 className="text-center text-xl font-semibold" style={{ color: 'var(--foreground)' }}>
            Redirecting to sign in
          </h2>
          <p className="text-muted-foreground text-center text-sm">
            You will be redirected to Authentik to continue.
          </p>
          {/* Footer */}
          <div className="text-muted-foreground mt-4 text-center text-xs">
            © {year} Mani. All rights reserved.
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
